/**
 * A single criteria table row wrapped with @dnd-kit/sortable
 * for drag-and-drop reordering.
 *
 * Renders in two modes:
 * - **Display** — read-only cells with edit/delete actions
 * - **Edit** — inline form cells (delegates to CriterionFormCells)
 */
import { useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FiEdit2, FiTrash2, FiMenu } from "react-icons/fi";
import { Modal } from "newui/components/ui/Modal";
import type { EvaluationCriterion } from "../../../../types/criteria.type";
import { EVALUATION_TYPE_OPTIONS } from "../../../../types/criteria.type";
import { CriterionFormCells } from "./CriterionFormCells";
import type { CriterionFormValues } from "./criteria-form.types";

interface SortableCriterionRowProps {
	canEdit: boolean;
	criterion: EvaluationCriterion;
	formValues: CriterionFormValues;
	isDraggable: boolean;
	isEditing: boolean;
	isSaving: boolean;
	onSetFormValues: React.Dispatch<React.SetStateAction<CriterionFormValues>>;
	onCancel: () => void;
	onDelete: (id: number) => void;
	onSaveEdit: () => void;
	onStartEdit: (c: EvaluationCriterion) => void;
	onUpdateField: (field: keyof CriterionFormValues, value: string) => void;
}

export const SortableCriterionRow: React.FC<SortableCriterionRowProps> = ({
	criterion,
	canEdit,
	isEditing,
	isSaving,
	formValues,
	onUpdateField,
	onSetFormValues,
	onSaveEdit,
	onCancel,
	onStartEdit,
	onDelete,
	isDraggable,
}) => {
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: criterion.id });

	const style: React.CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	// In edit mode, suppress dnd-kit's tabIndex/role attributes so they
	// don't interfere with natural tab order through the form inputs.
	const rowAttributes = isEditing ? {} : attributes;

	return (
		<tr
			ref={setNodeRef}
			style={style}
			{...rowAttributes}
			className="border-b border-gray-100"
			data-testid={`criterion-row-${criterion.id}`}
		>
			{/* Drag handle */}
			{canEdit && (
				<td className="w-8 py-2 pr-1" onClick={(e) => e.stopPropagation()}>
					{isDraggable ? (
						<button
							type="button"
							className="cursor-grab text-gray-400 hover:text-gray-600"
							data-testid={`drag-handle-${criterion.id}`}
							{...listeners}
						>
							<FiMenu size={14} />
						</button>
					) : (
						<span className="text-gray-200">
							<FiMenu size={14} />
						</span>
					)}
				</td>
			)}

			{isEditing ? (
				<CriterionFormCells
					formValues={formValues}
					isSaving={isSaving}
					onUpdateField={onUpdateField}
					onSetFormValues={onSetFormValues}
					onSave={onSaveEdit}
					onCancel={onCancel}
					testIdPrefix="edit"
				/>
			) : (
				<>
					<td className="py-2 pr-4 font-medium text-gray-900">
						{criterion.name}
					</td>
					<td className="py-2 pr-4 text-gray-500">
						{criterion.description || "-"}
					</td>
					<td className="py-2 pr-4 text-gray-500">
						{EVALUATION_TYPE_OPTIONS.find(
							(o) => o.value === criterion.rfx_evaluation_type_id
						)?.label ?? "Unknown"}
					</td>
					<td className="py-2 pr-4 text-right text-gray-900">
						{criterion.weight}%
					</td>
					{canEdit && (
						<td className="py-2 text-right">
							<div className="flex items-center justify-end gap-1">
								<button
									onClick={() => onStartEdit(criterion)}
									className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
									data-testid={`edit-btn-${criterion.id}`}
								>
									<FiEdit2 size={14} />
								</button>
								<button
									onClick={() => setShowDeleteConfirm(true)}
									disabled={isSaving}
									className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
									data-testid={`delete-btn-${criterion.id}`}
								>
									<FiTrash2 size={14} />
								</button>
							</div>

							{showDeleteConfirm && (
								<Modal
									isOpen
									onClose={() => setShowDeleteConfirm(false)}
									title="Delete Criterion"
									modalType="alert"
									iconType="warning"
									primaryButton={{
										text: "Delete",
										onClick: () => {
											onDelete(criterion.id);
											setShowDeleteConfirm(false);
										},
									}}
									secondaryButton={{
										text: "Cancel",
										onClick: () => setShowDeleteConfirm(false),
									}}
								>
									<p>
										Are you sure you want to delete <strong>{criterion.name}</strong>?
										This action cannot be undone.
									</p>
								</Modal>
							)}
						</td>
					)}
				</>
			)}
		</tr>
	);
};
